/**
 * Hooks for dashboard data fetching
 * Provides stats, recent activity, pending tasks, etc.
 */

import { useQuery } from "@tanstack/react-query";
import { frappeMethod, frappeListWithPagination } from "@/lib/erp/utils";
import { useAuthStore } from "@/lib/store/authStore";

// ============================================
// Recent Activity (using Version doctype)
// ============================================

export interface ActivityItem {
  id: string;
  doctype: string;
  docname: string;
  displayName: string; // Human-readable name (item_name, customer_name, etc.)
  action: string; // "Updated", "Created", etc.
  timestamp: string;
  user: {
    fullName: string;
    avatarUrl: string | null;
  };
  doc: {
    imageUrl: string | null; // Document image (item image, customer image)
  };
  icon: string;
  color: string;
  bgColor: string;
  href: string;
}

interface VersionDoc {
  name: string;
  ref_doctype: string;
  docname: string;
  owner: string;
  creation: string;
  data: string; // JSON string with change details
}

interface UserInfo {
  name: string;
  full_name: string;
  user_image: string | null;
}

interface ItemInfo {
  name: string;
  item_name: string;
}

interface CustomerInfo {
  name: string;
  customer_name: string;
  image: string | null;
}

// Doctype config for activity display
const doctypeConfig: Record<
  string,
  { icon: string; color: string; bgColor: string; getHref: (name: string) => string }
> = {
  "Sales Order": {
    icon: "ShoppingCart",
    color: "text-purple-600",
    bgColor: "bg-purple-100 dark:bg-purple-900/30",
    getHref: (name) => `/orders/${name}`,
  },
  Customer: {
    icon: "Users",
    color: "text-green-600",
    bgColor: "bg-green-100 dark:bg-green-900/30",
    getHref: (name) => `/customers/${name}`,
  },
  Item: {
    icon: "Package",
    color: "text-blue-600",
    bgColor: "bg-blue-100 dark:bg-blue-900/30",
    getHref: (name) => `/inventory/item/${name}`,
  },
  "Sales Invoice": {
    icon: "FileText",
    color: "text-amber-600",
    bgColor: "bg-amber-100 dark:bg-amber-900/30",
    getHref: (name) => `/selling/sales-invoice/${name}`,
  },
  "Stock Entry": {
    icon: "ArrowLeftRight",
    color: "text-teal-600",
    bgColor: "bg-teal-100 dark:bg-teal-900/30",
    getHref: (name) => `/list/Stock%20Entry/${name}`,
  },
  "Purchase Order": {
    icon: "ClipboardList",
    color: "text-indigo-600",
    bgColor: "bg-indigo-100 dark:bg-indigo-900/30",
    getHref: (name) => `/list/Purchase%20Order/${name}`,
  },
  "Payment Entry": {
    icon: "CreditCard",
    color: "text-emerald-600",
    bgColor: "bg-emerald-100 dark:bg-emerald-900/30",
    getHref: (name) => `/list/Payment%20Entry/${name}`,
  },
};

// List of doctypes to track in activity feed
const ACTIVITY_DOCTYPES = [
  "Sales Order",
  "Customer",
  "Item",
  "Sales Invoice",
  "Stock Entry",
  "Purchase Order",
  "Payment Entry",
];

// Convert technical field names to human-readable labels
function humanizeFieldName(field: string): string {
  // Common field name mappings
  const fieldMappings: Record<string, string> = {
    item_name: "name",
    item_code: "code",
    customer_name: "name",
    customer_type: "type",
    description: "description",
    image: "image",
    status: "status",
    grand_total: "total",
    total: "total",
    qty: "quantity",
    rate: "price",
    amount: "amount",
    delivery_date: "delivery date",
    transaction_date: "date",
    due_date: "due date",
    end_of_life: "end of life",
    is_stock_item: "stock setting",
    stock_uom: "unit",
    item_group: "category",
    brand: "brand",
    standard_rate: "price",
    valuation_rate: "valuation",
    customer_group: "group",
    territory: "territory",
    email_id: "email",
    mobile_no: "phone",
    address: "address",
    storefront_use_video_as_primary: "video display",
    item_gallery: "gallery",
  };

  // Check if we have a mapping
  if (fieldMappings[field]) {
    return fieldMappings[field];
  }

  // Convert snake_case to readable format
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toLowerCase());
}

// Determine action type from version data
function getActionFromVersionData(data: string): string {
  try {
    const parsed = JSON.parse(data);
    const changedFields = parsed.changed || [];
    const addedTables = parsed.added || [];
    const removedTables = parsed.removed || [];

    // Collect readable changes
    const changes: string[] = [];

    // Add changed field names (max 2)
    if (changedFields.length > 0) {
      const fieldNames = changedFields
        .slice(0, 2)
        .map((c: [string, unknown, unknown]) => humanizeFieldName(c[0]));
      changes.push(...fieldNames);
    }

    // Add table changes in a friendly way
    if (addedTables.length > 0) {
      // Get the table name and make it friendly
      const tableName = addedTables[0][0];
      if (tableName === "item_gallery" || tableName === "items") {
        changes.push(addedTables.length === 1 ? "added image" : "added images");
      } else if (tableName === "taxes") {
        changes.push("added tax");
      } else {
        changes.push("added items");
      }
    }

    if (removedTables.length > 0) {
      changes.push("removed items");
    }

    // Build the final action string
    if (changes.length === 0) {
      return "Updated";
    }

    // Format: "Updated price, quantity" or "Updated gallery"
    return `Updated ${changes.slice(0, 2).join(", ")}`;
  } catch {
    return "Updated";
  }
}

async function fetchRecentActivity(): Promise<ActivityItem[]> {
  // Fetch recent versions from Version doctype
  // Fetch more records to ensure we get variety across doctypes for tabs
  let versionsResult: { data: VersionDoc[]; total: number };

  try {
    versionsResult = await frappeListWithPagination<VersionDoc>("Version", {
      filters: [["ref_doctype", "in", ACTIVITY_DOCTYPES]],
      fields: ["name", "ref_doctype", "docname", "owner", "creation", "data"],
      order_by: "creation desc",
      limit_page_length: 50,
    });
  } catch (error) {
    console.error("[RecentActivity] Failed to fetch Version doctype:", error);
    // Return empty array - the UI will show "No recent activity"
    return [];
  }

  const versions = versionsResult.data;

  // Debug: Log if we got no versions
  if (versions.length === 0) {
    console.warn("[RecentActivity] No versions found for doctypes:", ACTIVITY_DOCTYPES);
  }

  // Get unique users
  const uniqueUsers = [...new Set(versions.map((v) => v.owner))];

  // Get unique items and customers for their images/names
  const uniqueItems = [...new Set(versions.filter((v) => v.ref_doctype === "Item").map((v) => v.docname))];
  const uniqueCustomers = [
    ...new Set(versions.filter((v) => v.ref_doctype === "Customer").map((v) => v.docname)),
  ];

  // Fetch all data in parallel
  const [usersResult, itemsResult, itemImagesResult, customersResult] = await Promise.all([
    // Users
    uniqueUsers.length > 0
      ? frappeListWithPagination<UserInfo>("User", {
          filters: [["name", "in", uniqueUsers]],
          fields: ["name", "full_name", "user_image"],
          limit_page_length: uniqueUsers.length,
        }).catch(() => ({ data: [] as UserInfo[], total_count: 0 }))
      : { data: [] as UserInfo[], total_count: 0 },

    // Items (for display names only)
    uniqueItems.length > 0
      ? frappeListWithPagination<ItemInfo>("Item", {
          filters: [["name", "in", uniqueItems]],
          fields: ["name", "item_name"],
          limit_page_length: uniqueItems.length,
        }).catch(() => ({ data: [] as ItemInfo[], total_count: 0 }))
      : { data: [] as ItemInfo[], total_count: 0 },

    // Item primary images (uses gallery + variant inheritance)
    uniqueItems.length > 0
      ? frappeMethod<Record<string, string | null>>(
          "zaviago_backend.api.item_list.get_primary_images_batch",
          { item_names: uniqueItems }
        ).catch(() => ({} as Record<string, string | null>))
      : ({} as Record<string, string | null>),

    // Customers (for images and display names)
    uniqueCustomers.length > 0
      ? frappeListWithPagination<CustomerInfo>("Customer", {
          filters: [["name", "in", uniqueCustomers]],
          fields: ["name", "customer_name", "image"],
          limit_page_length: uniqueCustomers.length,
        }).catch(() => ({ data: [] as CustomerInfo[], total_count: 0 }))
      : { data: [] as CustomerInfo[], total_count: 0 },
  ]);

  // Create lookup maps
  const userMap = new Map<string, UserInfo>();
  usersResult.data.forEach((user: UserInfo) => userMap.set(user.name, user));

  const itemMap = new Map<string, ItemInfo>();
  itemsResult.data.forEach((item: ItemInfo) => itemMap.set(item.name, item));

  // Item images from the batch endpoint (handles gallery + variant inheritance)
  const itemImageMap = itemImagesResult as Record<string, string | null>;

  const customerMap = new Map<string, CustomerInfo>();
  customersResult.data.forEach((customer: CustomerInfo) => customerMap.set(customer.name, customer));

  // Map versions to activity items
  const activities: ActivityItem[] = versions.map((version: VersionDoc) => {
    const config = doctypeConfig[version.ref_doctype] || {
      icon: "FileText",
      color: "text-gray-600",
      bgColor: "bg-gray-100 dark:bg-gray-800",
      getHref: () => "#",
    };

    const userInfo = userMap.get(version.owner);

    // Get document-specific info
    let displayName = version.docname;
    let docImageUrl: string | null = null;

    if (version.ref_doctype === "Item") {
      const itemInfo = itemMap.get(version.docname);
      if (itemInfo) {
        displayName = itemInfo.item_name || version.docname;
      }
      // Use the batch endpoint result for images (handles gallery + variant inheritance)
      docImageUrl = itemImageMap[version.docname] || null;
    } else if (version.ref_doctype === "Customer") {
      const customerInfo = customerMap.get(version.docname);
      if (customerInfo) {
        displayName = customerInfo.customer_name || version.docname;
        docImageUrl = customerInfo.image;
      }
    }

    // Determine action from version data
    const action = getActionFromVersionData(version.data);

    return {
      id: version.name,
      doctype: version.ref_doctype,
      docname: version.docname,
      displayName,
      action,
      timestamp: version.creation,
      user: {
        fullName: userInfo?.full_name || version.owner.split("@")[0],
        avatarUrl: userInfo?.user_image || null,
      },
      doc: {
        imageUrl: docImageUrl,
      },
      icon: config.icon,
      color: config.color,
      bgColor: config.bgColor,
      href: config.getHref(version.docname),
    };
  });

  // Return more activities so tabs have data to show
  return activities.slice(0, 30);
}

export function useRecentActivity() {
  const frappeClient = useAuthStore((s) => s.frappeClient);

  return useQuery({
    queryKey: ["dashboard-recent-activity"],
    queryFn: fetchRecentActivity,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
    // Wait for frappeClient to be ready (hydration complete)
    enabled: !!frappeClient,
  });
}

// ============================================
// Onboarding Progress — Intent Path Model
// ============================================

export interface IntentPath {
  id: string;
  steps: Array<{ id: string; completed: boolean; href: string }>;
  allComplete: boolean;
}

export interface OnboardingProgressResult {
  paths: IntentPath[];
  anyPathComplete: boolean;
}

async function fetchOnboardingProgress(): Promise<OnboardingProgressResult> {
  // Check if each doctype has at least 1 record.
  // Uses get_list with limit 1 instead of get_count (get_count is not
  // whitelisted on production Tenant Server, causing silent failures).
  const checkHasRecords = (doctype: string) =>
    frappeMethod<{ name: string }[]>("frappe.client.get_list", {
      doctype,
      limit_page_length: 1,
      fields: ["name"],
    }).then((r) => Array.isArray(r) && r.length > 0).catch(() => false);

  const [hasItems, hasCustomers, hasCRMLeads] = await Promise.all([
    checkHasRecords("Item"),
    checkHasRecords("Customer"),
    checkHasRecords("CRM Lead"),
  ]);

  // Note: "Chat with customers" path uses localStorage (chatwoot_onboarding_opened)
  // which is merged in the component layer via useChatwootOpened() hook.
  // The chat path step is marked incomplete here; the component overrides it.

  const paths: IntentPath[] = [
    {
      id: "selling",
      steps: [
        { id: "add-product", completed: hasItems, href: "/inventory/item/new" },
        { id: "add-customer", completed: hasCustomers, href: "/customers/new" },
      ],
      allComplete: hasItems && hasCustomers,
    },
    {
      id: "chat",
      steps: [
        // completed=false here; merged with localStorage in component
        { id: "open-inbox", completed: false, href: "/inbox" },
      ],
      allComplete: false, // overridden in component
    },
    {
      id: "contacts",
      steps: [
        { id: "add-customer", completed: hasCustomers, href: "/customers/new" },
        { id: "add-lead", completed: hasCRMLeads, href: "/crm" },
      ],
      allComplete: hasCustomers && hasCRMLeads,
    },
  ];

  const anyPathComplete = paths.some((p) => p.allComplete);

  return { paths, anyPathComplete };
}

export function useOnboardingProgress() {
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["dashboard-onboarding"],
    queryFn: fetchOnboardingProgress,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: hasHydrated && isAuthenticated,
  });
}

// ============================================
// Global Search
// ============================================

interface SearchResult {
  doctype: string;
  name: string;
  title: string;
  description?: string;
  href: string;
  icon: string;
}

interface CustomerSearchResult {
  name: string;
  customer_name: string;
}

interface ItemSearchResult {
  name: string;
  item_name: string;
  item_code: string;
}

interface OrderSearchResult {
  name: string;
  customer_name: string;
}

export async function globalSearch(query: string): Promise<SearchResult[]> {
  if (!query || query.length < 2) return [];

  const results: SearchResult[] = [];

  // Search across multiple doctypes
  const [customersResult, itemsResult, ordersResult, invoicesResult] = await Promise.all([
    frappeListWithPagination<CustomerSearchResult>("Customer", {
      fields: ["name", "customer_name"],
      filters: [["customer_name", "like", `%${query}%`]],
      limit_page_length: 5,
    }).catch(() => ({ data: [] as CustomerSearchResult[], total_count: 0 })),

    frappeListWithPagination<ItemSearchResult>("Item", {
      fields: ["name", "item_name", "item_code"],
      or_filters: [
        ["item_name", "like", `%${query}%`],
        ["item_code", "like", `%${query}%`],
      ],
      limit_page_length: 5,
    }).catch(() => ({ data: [] as ItemSearchResult[], total_count: 0 })),

    frappeListWithPagination<OrderSearchResult>("Sales Order", {
      fields: ["name", "customer_name"],
      or_filters: [
        ["name", "like", `%${query}%`],
        ["customer_name", "like", `%${query}%`],
      ],
      limit_page_length: 5,
    }).catch(() => ({ data: [] as OrderSearchResult[], total_count: 0 })),

    frappeListWithPagination<OrderSearchResult>("Sales Invoice", {
      fields: ["name", "customer_name"],
      or_filters: [
        ["name", "like", `%${query}%`],
        ["customer_name", "like", `%${query}%`],
      ],
      limit_page_length: 5,
    }).catch(() => ({ data: [] as OrderSearchResult[], total_count: 0 })),
  ]);

  customersResult.data.forEach((c: CustomerSearchResult) => {
    results.push({
      doctype: "Customer",
      name: c.name,
      title: c.customer_name || c.name,
      href: `/customers/${c.name}`,
      icon: "Users",
    });
  });

  itemsResult.data.forEach((i: ItemSearchResult) => {
    results.push({
      doctype: "Item",
      name: i.name,
      title: i.item_name || i.name,
      description: i.item_code,
      href: `/inventory/item/${i.name}`,
      icon: "Package",
    });
  });

  ordersResult.data.forEach((o: OrderSearchResult) => {
    results.push({
      doctype: "Sales Order",
      name: o.name,
      title: o.name,
      description: o.customer_name,
      href: `/orders/${o.name}`,
      icon: "ShoppingCart",
    });
  });

  invoicesResult.data.forEach((inv: OrderSearchResult) => {
    results.push({
      doctype: "Sales Invoice",
      name: inv.name,
      title: inv.name,
      description: inv.customer_name,
      href: `/selling/sales-invoice/${inv.name}`,
      icon: "FileText",
    });
  });

  return results;
}

export function useGlobalSearch(query: string) {
  const frappeClient = useAuthStore((s) => s.frappeClient);

  return useQuery({
    queryKey: ["global-search", query],
    queryFn: () => globalSearch(query),
    // Wait for frappeClient AND query to be valid
    enabled: !!frappeClient && query.length >= 2,
    staleTime: 30 * 1000, // 30 seconds
  });
}
